import { NextSeoProps } from 'next-seo';

const defaultTags = {
  additionalLinkTags: [
    {
      rel: 'apple-touch-icon',
      sizes: '57x57',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-57.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '60x60',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-60.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '72x72',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-72.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '76x76',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-76.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '114x114',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-114.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '120x120',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-120.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '144x144',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-144.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '152x152',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-152.png',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-180.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-192.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-32.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '96x96',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-96.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: 'https://res.cloudinary.com/samvibes/image/upload/landing/favicon/app-icon-16.png',
    },
    {
      rel: 'manifest',
      href: '/favicons/manifest.json',
    },
    {
      rel: 'mask-icon',
      href: '/favicons/safari-pinned-tab.svg',
      color: '#254DDA',
    },
    {
      rel: 'shortcut icon',
      href: '/favicons/favicon.ico',
      color: '#254DDA',
    },
  ],
  additionalMetaTags: [
    {
      name: 'msapplication-TileColor',
      content: '#254DDA',
    },
    {
      name: 'msapplication-TileImage',
      content: '/ms-icon-144x144.png',
    },
    {
      name: 'theme-color',
      content: '#254DDA',
    },
    {
      name: 'application-name',
      content: 'Sketchnote App',
    },
    {
      name: 'apple-mobile-web-app-capable',
      content: 'yes',
    },
    {
      name: 'apple-mobile-web-app-status-bar-style',
      content: 'default',
    },
    {
      name: 'apple-mobile-web-app-title',
      content: 'Sketchnote App',
    },
    {
      name: 'format-detection',
      content: 'telephone=no',
    },
    {
      name: 'msapplication-tap-highlight',
      content: 'no',
    },
    {
      name: 'mobile-web-app-capable',
      content: 'yes',
    },
  ],
};

const Pages = [
  'default',
  'dashboard',
  'home',
  'about',
  'pricing',
  'grants',
  'catalogue',
  'howItWorks',
  'meetTheMentors',
  'product',
  'learn',
  'create',
  'docs',
  'boards',
  'autoCompleteLegal',
  'aiRecommendations',
  'gamification',
  'help',
  'sketchnotePartnersFund',
  'cookiesPolicy',
  'privacyPolicy',
  'termsOfService',
  'skectnote100KChallenge',
  'thankyou',
] as const;

type ElementType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer ElementType>
  ? ElementType
  : never;

export const PageMetaData: { [key in ElementType<typeof Pages>]: NextSeoProps } = {
  default: {
    title: 'Sketchnote | The only app you need!',
    description:
      'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
    openGraph: {
      url: 'https://sketchnote.co/',
      title: 'Sketchnote | The only app you need!',
      description:
        'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/Banner.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  dashboard: {
    title: 'Sketchnote | Dashboard',
    description: 'Welcome to sketchnote',
    openGraph: {
      url: 'https://app.sketchnote.co/dashboard',
      title: 'Sketchnote | The only app you need!',
      description:
        'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/Banner.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content: 'Startup Ed-tech, productivity tool',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  home: {
    title: 'Sketchnote | Startup lessons and productivity tools',
    description:
      'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
    openGraph: {
      url: 'https://sketchnote.co/',
      title: 'Sketchnote | The only app you need!',
      description:
        'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/Banner.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content: 'Startup Ed-tech, productivity tool',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  about: {
    title: 'About us | Sketchnote',
    description:
      'Sketchnote empowers entrepreneurs and teams to build their dream startup, one lesson at a time. Learn about our mission and how we help your startup',
    openGraph: {
      url: 'https://sketchnote.co/about',
      title: 'About us | Sketchnote',
      description:
        'Sketchnote empowers entrepreneurs and teams to build their dream startup, one lesson at a time. Learn about our mission and how we help your startup',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/home-page.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content: 'Startup Ed-tech, productivity tool',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  pricing: {
    title: 'Sketchnote | Pricing',
    description:
      "Find out Sketchnote's pricing plans for solo usage, team memberships, enterprise solutions, as well as special pricing for Sketchnote's beta launch.",
    openGraph: {
      url: 'https://sketchnote.co/pricing',
      title: 'Sketchnote | Pricing',
      description:
        "Find out Sketchnote's pricing plans for solo usage, team memberships, enterprise solutions, as well as special pricing for Sketchnote's beta launch.",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/home-page.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content:
          'Startup tool pricing, online productivity tool pricing, best markdown editor pricing, team planner tool pricing',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  grants: {
    title: 'Sketchnote Grants, a program for upcoming startups',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://sketchnote.co/grants',
      title: 'Sketchnote Grants, a program for upcoming startups',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote grants preview',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  sketchnotePartnersFund: {
    title: 'Southeast Asia fund for seed startups | Sketchnote',
    description:
      'Introducing Sketchnote Partners Fund, a financing vehicle for exceptional first-time entrepreneurs building first-rate startups',
    openGraph: {
      url: 'https://sketchnote.co/sketchnote-funding',
      title: 'Southeast Asia fund for seed startups | Sketchnote',
      description:
        'Apply for funding on Sketchnote, a unique opportunity for exceptional first-time entrepreneurs building first-rate startups',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/landing/preview-image/sketchnote-funding.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote funding',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  catalogue: {
    title: 'Sketchnote | Template library',
    description:
      'Find an extensive catalog of lessons for every startup stage, from Fundraising to Product Development and Personal Development with our Template library',
    openGraph: {
      url: 'https://sketchnote.co/catalog',
      title: 'Sketchnote | Template library',
      description:
        'The #1 collaboration tool for teams. Plan and track your tasks together with 100+ templates for all business needs. Get started for free',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/Banner.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content: 'Edtech templates, Online courses, Startup templates',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  howItWorks: {
    title: 'How it works | Sketchnote',
    description:
      "Read this quick primer on how you can use Sketchnote's various features for your startup education and growth journey",
    openGraph: {
      url: 'https://sketchnote.co/how-it-works',
      title: 'How it works | Sketchnote',
      description:
        "Read this quick primer on how you can use Sketchnote's various features for your startup education and growth journey",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/home-page.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  meetTheMentors: {
    title: 'Meet the mentors | Sketchnote',
    description:
      "Learn more about Sketchnote's startup mentors and their specialities in this fun comic",
    openGraph: {
      url: 'https://sketchnote.co/meet-the-mentors',
      title: 'Meet the mentors | Sketchnote',
      description:
        "Learn more about Sketchnote's startup mentors and their specialities in this fun comic",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/home-page.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  product: {
    title: 'Sketchnote | Product',
    description:
      "From startup lessons, exercises, markdown editor, and storage space, find every tool entrepreneurs need with Sketchnote's power-packed features",
    openGraph: {
      url: 'https://sketchnote.co/product',
      title: 'Sketchnote | Product',
      description:
        "From startup lessons, exercises, markdown editor, and storage space, find every tool entrepreneurs need with Sketchnote's power-packed features",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/product-home.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  learn: {
    title: 'Free startup course and resources | Sketchnote',
    description:
      'Lessons and modules on startup education for budding entrepreneurs, seasoned startuppers, VCs and students',
    nofollow: true,
    openGraph: {
      url: 'https://sketchnote.co/learn',
      title: 'Free startup course and resources | Sketchnote',
      description:
        'Lessons and modules on startup education for budding entrepreneurs, seasoned startuppers, VCs and students',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/learn.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  create: {
    title: 'Document management for startups | Sketchnote',
    description:
      'Find the building blocks for creating your startup smoothly with the best online collaboration tool and document management system',
    openGraph: {
      url: 'https://sketchnote.co/document-management-system',
      title: 'Document management for startups | Sketchnote',
      description:
        'Find the building blocks for creating your startup smoothly with the best online collaboration tool and document management system',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/create.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  docs: {
    title: 'Document storage for startups | Sketchnote',
    description:
      'An all-new collaborative space for your startup, armed with the best markdown tools and document management system',
    openGraph: {
      url: 'https://sketchnote.co/storage',
      title: 'Document storage for startups | Sketchnote',
      description:
        'An all-new collaborative space for your startup, armed with the best markdown tools and document management system',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/docs.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  boards: {
    title: 'Sketchnote | Kanban and Calendar',
    description:
      "Plan and track your startup's projects and milestones along with your team using agile project management tools and a robust calendar mode",
    openGraph: {
      url: 'https://sketchnote.co/online-collaboration-tool',
      title: 'Sketchnote | Docs',
      description:
        "Plan and track your startup's projects and milestones along with your team using agile project management tools and a robust calendar mode",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/boards.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  autoCompleteLegal: {
    title: 'Sketchnote | Legal docs',
    description:
      'The one-stop online service for startup paperwork and autocompleting legal documents for your organization',
    openGraph: {
      url: 'https://sketchnote.co/autocomplete-legal-documents',
      title: 'Sketchnote | Legal docs',
      description:
        'The one-stop online service for startup paperwork and autocompleting legal documents for your organization',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/legal.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  aiRecommendations: {
    title: 'Sketchnote | AI recommendations',
    description: 'The easiest way to AI generate copy and content for your startup',
    openGraph: {
      url: 'https://sketchnote.co/ai-recommendations',
      title: 'Sketchnote | Docs',
      description: 'The easiest way to AI generate copy and content for your startup',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/ai.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  gamification: {
    title: 'Gamification platform for startups | Sketchnote',
    description:
      'Win points for learning and creating your startup, brick by brick, and redeem them for cash prize grant rewards',
    openGraph: {
      url: 'https://sketchnote.co/gamification',
      title: 'Gamification platform for startups | Sketchnote',
      description:
        'Win points for learning and creating your startup, brick by brick, and redeem them for cash prize grant rewards',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/gamification.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  help: {
    title: 'Explore our help resources | Sketchnote',
    description:
      "Need to know more about Sketchnote's lessons, or about our online productivity tools, and autocomplete startup document features? Find resources here",
    openGraph: {
      url: 'https://app.sketchnote.co/help',
      title: 'Explore our help resources | Sketchnote',
      description:
        "Need to know more about Sketchnote's lessons, or about our online productivity tools, and autocomplete startup document features? Find resources here",
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/home-page.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote banner',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'keywords',
        content: 'Help centre, help page',
      },
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },

  thankyou: {
    title: 'Thankyou | Sketchnote',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://app.sketchnote.co/thankyou',
      title: 'Thankyou | Sketchnote',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote thank you',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },

  termsOfService: {
    title: 'Terms of service | Sketchnote',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://sketchnote.co/terms-of-service',
      title: 'Terms of service | Sketchnote',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote terms of service',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },

  cookiesPolicy: {
    title: 'Cookies | Sketchnote',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://sketchnote.co/cookies-policy',
      title: 'Cookies | Sketchnote',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote cookies policy',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
  privacyPolicy: {
    title: 'Privacy policy | Sketchnote',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://sketchnote.co/privacy-policy',
      title: 'Privacy policy | Sketchnote',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote privacy policy',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },

  skectnote100KChallenge: {
    title: 'Acceleration program for Asian startups | Sketchnote',
    description: 'Apply for and win monetary grants from Sketchnote',
    openGraph: {
      url: 'https://sketchnote.co/sketchnote-100k-challenge',
      title: 'Acceleration program for Asian startups | Sketchnote',
      description: 'Apply for and win monetary grants from Sketchnote',
      images: [
        {
          url: 'https://res.cloudinary.com/samvibes/image/upload/c_scale,w_1280/landing/preview-image/grants.png',
          width: 1280,
          height: 672,
          alt: 'Sketchnote 100k challenge',
        },
      ],
      site_name: 'Sketchnote',
    },
    twitter: {
      cardType: 'summary_large_image',
      handle: '@SketchnoteCo',
      site: '@SketchnoteCo',
    },
    ...defaultTags,
    additionalMetaTags: [
      ...defaultTags.additionalMetaTags,
      {
        name: 'language',
        content: 'English',
      },
      {
        httpEquiv: 'content-type',
        content: 'text/html; charset=utf-8',
      },
    ],
  },
};
